import React, { useEffect, useRef, useState, useCallback } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import {
  BarChart3, BookOpen, ClipboardList, DollarSign, Eye,
  Layout, LayoutDashboard, Network, Settings, Shield, Users, Zap,
} from 'lucide-react';
import { usePortalStore } from '../store';
import { TopBar } from './TopBar';
import { BottomNav } from './BottomNav';
import { AICoFounderPanel } from './AICoFounderPanel';
import { DashboardView } from './screens/DashboardView';
import { OperationsHubView } from './screens/OperationsHubView';
import { StrategyView } from './screens/StrategyView';
import { DepartmentView } from './screens/DepartmentView';
import { WatchtowerView } from './screens/WatchtowerView';
import { GovernanceLayer } from './screens/GovernanceLayer';
import { KnowledgeRoomView } from './screens/KnowledgeRoomView';
import { SettingsView } from './screens/SettingsView';
import { LogbookView } from './screens/LogbookView';
import { ProductBacklogRoomView } from './screens/ProductBacklogRoomView';
import { TreasuryView } from './screens/TreasuryView';
import { BoardView } from './screens/BoardView';
import { LibraryProposalsView } from './screens/LibraryProposalsView';
import { ToastContainer } from './ToastContainer';

/* Space and Sand colour tokens for GSAP mode tween (Style Guide §12.1)
   A single 600ms tween on CSS custom properties cascades the full palette. */
const SPACE_TOKENS = {
  '--bg-primary':   '#0E1420',
  '--bg-secondary': '#1A2332',
  '--bg-elevated':  '#253040',
  '--bg-panel':     '#1C2A3A',
  '--bg-depth-3':   '#2D3D50',
  '--accent-amber': '#F0A500',
  '--accent-teal':  '#3ECFCF',
  '--text-primary': '#E8EDF2',
  '--text-secondary': '#8FA0B4',
  '--text-tertiary':  '#5A6B7E',
  '--border-subtle':  'rgba(255, 255, 255, 0.06)',
  '--border-default': 'rgba(255, 255, 255, 0.10)',
  '--border-strong':  'rgba(255, 255, 255, 0.18)',
  '--hover-overlay':  'rgba(240, 165, 0, 0.08)',
  '--active-overlay': 'rgba(240, 165, 0, 0.16)',
  '--focus-ring':     '#F0A500',
} as const;

const SAND_TOKENS = {
  '--bg-primary':   '#F5EDD8',
  '--bg-secondary': '#E8D8BE',
  '--bg-elevated':  '#D9C8A5',
  '--bg-panel':     '#EAD9C0',
  '--bg-depth-3':   '#CCBA98',
  '--accent-amber': '#C47A00',
  '--accent-teal':  '#0B7878',
  '--text-primary': '#1C1209',
  '--text-secondary': '#6B4F32',
  '--text-tertiary':  '#9C7B58',
  '--border-subtle':  'rgba(28, 18, 9, 0.06)',
  '--border-default': 'rgba(28, 18, 9, 0.12)',
  '--border-strong':  'rgba(28, 18, 9, 0.22)',
  '--hover-overlay':  'rgba(196, 122, 0, 0.08)',
  '--active-overlay': 'rgba(196, 122, 0, 0.16)',
  '--focus-ring':     '#C47A00',
} as const;

export type PortalMode = 'space' | 'sand';

/* Sidebar navigation items — all 17 screens (UX spec §3 breakpoint navigation).
   Mobile BottomNav shows the top 5; sidebar shows all at md+. */
interface SidebarItem { id: string; label: string; icon: React.ReactNode }

function buildSidebarItems(gatesBadge: number, notifBadge: number): (SidebarItem & { badge?: number })[] {
  const iconCls = 'w-4 h-4 flex-shrink-0';
  return [
    { id: 'dashboard',         label: 'Dashboard',   icon: <LayoutDashboard className={iconCls} />, badge: (notifBadge + gatesBadge) || undefined },
    { id: 'strategy',          label: 'Strategy',    icon: <Layout          className={iconCls} /> },
    { id: 'operations',        label: 'Operations',  icon: <Zap             className={iconCls} /> },
    { id: 'governance',        label: 'Governance',  icon: <Shield          className={iconCls} /> },
    { id: 'board',             label: 'Board',       icon: <Users           className={iconCls} /> },
    { id: 'logbook',           label: 'Logbook',     icon: <ClipboardList   className={iconCls} /> },
    { id: 'watchtower',        label: 'Watchtower',  icon: <Eye             className={iconCls} /> },
    { id: 'knowledge-room',    label: 'Knowledge',   icon: <Network         className={iconCls} /> },
    { id: 'backlog',           label: 'Backlog',     icon: <BarChart3       className={iconCls} /> },
    { id: 'library-proposals', label: 'Proposals',   icon: <BookOpen        className={iconCls} /> },
    { id: 'treasury',          label: 'Treasury',    icon: <DollarSign      className={iconCls} /> },
    { id: 'settings',          label: 'Settings',    icon: <Settings        className={iconCls} /> },
  ];
}

/* SidebarNav — hidden on mobile, icon-only at md (64px), icon+label at lg (192px),
   full with badges at xl (240px). Matches UX spec §3 navigation breakpoint pattern. */
const SidebarNav: React.FC<{
  currentView: string;
  navigateTo: (v: string) => void;
  gatesBadge: number;
  notifBadge: number;
}> = ({ currentView, navigateTo, gatesBadge, notifBadge }) => {
  const items = buildSidebarItems(gatesBadge, notifBadge);
  return (
    <aside
      className="hidden md:flex md:flex-col md:w-16 lg:w-48 xl:w-60 flex-shrink-0 overflow-y-auto overflow-x-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderRight: '1px solid var(--border-default)' }}
      aria-label="Sidebar navigation"
    >
      {items.map(item => {
        const isActive = currentView === item.id;
        return (
          <button
            key={item.id}
            onClick={() => navigateTo(item.id)}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
            className="flex items-center gap-2.5 px-3 py-2 min-h-[44px] w-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[--accent-amber]"
            style={{
              fontFamily:      'var(--font-ui)',
              fontSize:        '0.8125rem',
              fontWeight:      500,
              color:           isActive ? 'var(--accent-amber)' : 'var(--text-secondary)',
              backgroundColor: isActive ? 'var(--active-overlay)' : 'transparent',
            }}
            onMouseEnter={e => {
              if (!isActive) {
                (e.currentTarget as HTMLButtonElement).style.color           = 'var(--text-primary)';
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--hover-overlay)';
              }
            }}
            onMouseLeave={e => {
              if (!isActive) {
                (e.currentTarget as HTMLButtonElement).style.color           = 'var(--text-secondary)';
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
              }
            }}
          >
            {item.icon}
            {/* Label: hidden on icon-only (md), visible at lg+ */}
            <span className="hidden lg:block truncate flex-1">{item.label}</span>
            {/* Badge: shown at xl+ only to avoid clutter on icon-only sidebar */}
            {item.badge != null && item.badge > 0 && (
              <span className="badge-count hidden xl:inline-flex ml-auto">{item.badge}</span>
            )}
          </button>
        );
      })}
    </aside>
  );
};

export const Shell: React.FC = () => {
  const {
    currentView,
    navigateTo,
    gates,
    notifications,
    loadCompany,
    loadDepartments,
    loadGates,
    loadDocuments,
    loadSignals,
    loadDecisions,
    loadWaveStatus,
    loadActionItems,
    loadIncidents,
    loadTeamCapacity,
    loadPrinciples,
    loadAuthorityMatrix,
    loadComplianceStatuses,
    loadRisks,
    loadADRHistory,
    loadLucasChat,
  } = usePortalStore();

  const [panelOpen, setPanelOpen] = useState(true);

  /* Portal mode — Space (dark) default, Sand (light) on toggle */
  const [mode, setMode] = useState<PortalMode>(() => {
    const saved = localStorage.getItem('zpc-portal-mode') as PortalMode | null;
    if (saved === 'sand' || saved === 'space') return saved;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'sand' : 'space';
  });

  /* Lenis instance ref — inertial scroll on main content area */
  const lenisRef = useRef<Lenis | null>(null);
  const mainRef  = useRef<HTMLDivElement>(null);
  const rafRef   = useRef<number>(0);

  /* GSAP mode tween — 600ms power2.inOut on CSS custom properties (§12.1) */
  const applyMode = useCallback((nextMode: PortalMode, animate: boolean) => {
    const tokens = nextMode === 'sand' ? SAND_TOKENS : SPACE_TOKENS;
    const root   = document.documentElement;
    if (animate) {
      /* Tween each custom property. GSAP CSSPlugin handles color interpolation. */
      const proxy: Record<string, string> = {};
      (Object.keys(tokens) as Array<keyof typeof tokens>).forEach(k => {
        proxy[k] = getComputedStyle(root).getPropertyValue(k).trim();
      });
      gsap.to(proxy, {
        duration: 0.6,
        ease: 'power2.inOut',
        ...tokens,
        onUpdate() {
          (Object.keys(proxy) as string[]).forEach(k => {
            root.style.setProperty(k, proxy[k]);
          });
        },
        onComplete() {
          /* Clean inline overrides — let the data-mode attribute take over */
          (Object.keys(tokens) as string[]).forEach(k => {
            root.style.removeProperty(k);
          });
          root.setAttribute('data-mode', nextMode);
        },
      });
    } else {
      (Object.keys(tokens) as Array<keyof typeof tokens>).forEach(k => {
        root.style.removeProperty(k);
      });
      root.setAttribute('data-mode', nextMode);
    }
    localStorage.setItem('zpc-portal-mode', nextMode);
  }, []);

  /* Apply initial mode without animation */
  useEffect(() => {
    applyMode(mode, false);
  }, []);

  const toggleMode = useCallback(() => {
    const next: PortalMode = mode === 'space' ? 'sand' : 'space';
    setMode(next);
    applyMode(next, true);
  }, [mode, applyMode]);

  /* Lenis inertial scroll — applied to the main content area */
  useEffect(() => {
    if (!mainRef.current) return;
    const lenis = new Lenis({
      wrapper:   mainRef.current,
      content:   mainRef.current.firstElementChild as HTMLElement,
      duration:  1.2,
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    function raf(time: number) {
      lenis.raf(time);
      rafRef.current = requestAnimationFrame(raf);
    }
    rafRef.current = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafRef.current);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  /* Data initialisation */
  useEffect(() => {
    loadCompany();
    loadDepartments();
    loadGates();
    loadDocuments();
    loadSignals();
    loadDecisions();
    loadWaveStatus();
    loadActionItems();
    loadIncidents();
    loadTeamCapacity();
    loadPrinciples();
    loadAuthorityMatrix();
    loadComplianceStatuses();
    loadRisks();
    loadADRHistory();
    loadLucasChat();
  }, []);

  /* Canvas overflow-hidden needed for its internal fixed-height grid */
  const isCanvasView = currentView === 'strategy';

  const gatesBadge = gates.filter(g => g.status === 'pending').length;
  const notifBadge = notifications.length;

  const renderScreen = () => {
    switch (currentView) {
      case 'dashboard':          return <DashboardView />;
      case 'strategy':           return <StrategyView />;
      case 'operations':         return <OperationsHubView />;
      case 'governance':         return <GovernanceLayer />;
      case 'board':              return <BoardView />;
      case 'logbook':            return <LogbookView />;
      case 'watchtower':         return <WatchtowerView />;
      case 'knowledge-room':     return <KnowledgeRoomView />;
      case 'backlog':            return <ProductBacklogRoomView />;
      case 'library-proposals':  return <LibraryProposalsView />;
      case 'treasury':           return <TreasuryView />;
      case 'settings':           return <SettingsView />;
      case 'department':         return <DepartmentView />;
      /* Legacy IDs — redirect to merged parents */
      case 'canvas':
      case 'documents':          return <StrategyView />;
      case 'command-centre':
      case 'company-overview':
      case 'gates':              return <DashboardView />;
      case 'workflow-monitor':
      case 'process-obs':        return <OperationsHubView />;
      default:                   return <DashboardView />;
    }
  };

  return (
    /* depth-1 = Command Centre / shell chrome level (§10.2) */
    <div
      className="flex flex-col h-screen"
      style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
    >
      <TopBar
        panelOpen={panelOpen}
        onTogglePanel={() => setPanelOpen(!panelOpen)}
        mode={mode}
        onToggleMode={toggleMode}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — always visible so navigation works from every view */}
        <SidebarNav
          currentView={currentView}
          navigateTo={navigateTo}
          gatesBadge={gatesBadge}
          notifBadge={notifBadge}
        />

        {isCanvasView ? (
          /* Canvas needs overflow-hidden so its internal grid fills exactly */
          <div className="flex-1 overflow-hidden">
            {renderScreen()}
          </div>
        ) : (
          <>
            {/* Main content — Lenis target.
                pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] clears the fixed BottomNav on mobile.
                md:pb-0 removes the clearance once BottomNav is hidden. */}
            <div ref={mainRef} className="flex-1 overflow-auto min-w-0">
              <div className="p-3 md:p-4 lg:p-6 pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] md:pb-0 rise-in-stagger">
                {renderScreen()}
              </div>
            </div>

            {/* AI Co-Founder panel — hidden below lg to preserve content space on tablet/mobile */}
            {panelOpen && (
              <div className="hidden lg:flex w-96 flex-col zpc-panel">
                <AICoFounderPanel currentView={currentView} />
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
      <ToastContainer />
    </div>
  );
};
